import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Button, Typography, TextField, Chip, Alert, Card,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Select, MenuItem, Divider, CircularProgress, Paper,
  Grid, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PaymentIcon from "@mui/icons-material/Payment";
import { useParams, useNavigate } from "react-router-dom";
import { useCreateShopInvoiceMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { IShopOrder, IShopOrderItem } from "../../../Api/Interface/api.interface";
import dayjs from "dayjs";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const STATUS_MAP: Record<string, { bg: string; color: string; label: string }> = {
  pending:    { bg: "#FFF3E0", color: "#E65100", label: "Submitted" },
  confirmed:  { bg: "#E3F2FD", color: "#0D47A1", label: "Confirmed" },
  processing: { bg: "#F3E5F5", color: "#6A1B9A", label: "Under Review" },
  delivered:  { bg: "#E8F5E9", color: "#1B5E20", label: "Delivered" },
  cancelled:  { bg: "#FFEBEE", color: "#B71C1C", label: "Cancelled" },
};
const STATUSES = ["pending", "confirmed", "processing", "delivered", "cancelled"];
const STATUS_FLOW = ["pending", "confirmed", "delivered"];
const STATUS_FLOW_LABELS = ["Submitted", "Confirmed", "Delivered"];

export default function ShopOrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [createShopInvoice] = useCreateShopInvoiceMutation();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<IShopOrder | null>(null);
  const [shop, setShop] = useState<any>(null);
  const [editItems, setEditItems] = useState<IShopOrderItem[]>([]);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [routeAssignment, setRouteAssignment] = useState<any>(null);
  const [availableRoutes, setAvailableRoutes] = useState<any[]>([]);
  const [routeDialog, setRouteDialog] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState<number | "">("");
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState("");
  const [assigningRoute, setAssigningRoute] = useState(false);
  const [routeWarning, setRouteWarning] = useState("");
  const [addProductDialog, setAddProductDialog] = useState(false);
  const [newProductName, setNewProductName] = useState("");
  const [newProductQty, setNewProductQty] = useState("1");
  const [newProductPrice, setNewProductPrice] = useState("0");
  const [newProductUnit, setNewProductUnit] = useState("lb");
  const [noteHistory, setNoteHistory] = useState<string[]>([]);
  const [newNote, setNewNote] = useState("");
  const [creditStatus, setCreditStatus] = useState<any>(null);

  const token = localStorage.getItem("token") || "";
  const headers = { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${token}` };

  const loadOrder = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/shop-orders/${orderId}/detail`, { headers });
      const data = await res.json();
      if (data.success) {
        setOrder(data.data);
        setShop(data.shop);
        setEditItems(data.data.items.map((it: IShopOrderItem) => ({ ...it })));
        const notes = data.data.notes || "";
        setEditNotes(notes);
        setNoteHistory(notes ? notes.split("\n").filter((n: string) => n.trim()) : []);
        setEditStatus(data.data.status);
        setRouteAssignment(data.route_assignment || null);
        setAvailableRoutes(data.available_routes || []);
        if (data.credit_status) setCreditStatus(data.credit_status);
      }
    } catch { showErrorMessage("Failed to load order"); }
    finally { setLoading(false); }
  }, [orderId]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  const editTotal = editItems.reduce((s, it) => s + it.subtotal, 0);
  const itemsWithWarning = editItems.filter(it => it.price === 0);

  const handleQtyChange = (idx: number, val: string) => {
    setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, qty: parseFloat(val) || 0, subtotal: (parseFloat(val) || 0) * it.price } : it));
  };
  const handlePriceChange = (idx: number, val: string) => {
    setEditItems(prev => prev.map((it, i) => i === idx ? { ...it, price: parseFloat(val) || 0, subtotal: it.qty * (parseFloat(val) || 0) } : it));
  };
  const handleRemoveItem = (idx: number) => { setEditItems(prev => prev.filter((_, i) => i !== idx)); };
  const handleDuplicateItem = (idx: number) => {
    setEditItems(prev => { const copy = { ...prev[idx] }; const n = [...prev]; n.splice(idx + 1, 0, copy); return n; });
  };

  const handleAddProduct = () => {
    if (!newProductName.trim()) return;
    const qty = parseFloat(newProductQty) || 1;
    const price = parseFloat(newProductPrice) || 0;
    setEditItems(prev => [...prev, { product_id: 0, name: newProductName.trim(), qty, price, subtotal: qty * price, unit: newProductUnit } as any]);
    setNewProductName(""); setNewProductQty("1"); setNewProductPrice("0"); setNewProductUnit("lb");
    setAddProductDialog(false);
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    const timestamp = dayjs().format("MMM D, h:mm A");
    const entry = `[${timestamp}] ${newNote.trim()}`;
    const updated = [...noteHistory, entry];
    setNoteHistory(updated);
    setEditNotes(updated.join("\n"));
    setNewNote("");
  };

  const handleSave = async (overrideStatus?: string) => {
    if (editItems.length === 0) { showErrorMessage("Order must have at least one item"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/shop-orders/${orderId}`, {
        method: "PUT", headers,
        body: JSON.stringify({ items: editItems, total: editTotal, notes: editNotes, status: overrideStatus || editStatus }),
      });
      const data = await res.json();
      if (data.success) {
        showMessage(data.message || "Order updated");
        if (data.route_warning) setRouteWarning(data.route_warning);
        // Auto-generate invoice when confirming
        if (overrideStatus === "confirmed") {
          try {
            const invRes = await createShopInvoice({ shop_order_id: Number(orderId) }).unwrap();
            if (invRes.success && !invRes.existing) showMessage("Invoice generated: " + invRes.data.invoice_number);
          } catch {}
        }
        loadOrder();
      } else showErrorMessage(data.message || "Failed");
    } catch (e: any) { showErrorMessage(e.message); }
    finally { setSaving(false); }
  };

  const handleCancel = async () => {
    setSaving(true);
    try {
      const notes = cancelReason ? `${editNotes ? editNotes + "\n" : ""}Cancelled: ${cancelReason}` : editNotes;
      const res = await fetch(`${API_URL}/shop-orders/${orderId}`, { method: "PUT", headers, body: JSON.stringify({ items: editItems, total: editTotal, notes, status: "cancelled" }) });
      const data = await res.json();
      if (data.success) { showMessage("Order cancelled"); setCancelDialog(false); loadOrder(); }
      else showErrorMessage(data.message || "Failed");
    } catch (e: any) { showErrorMessage(e.message); }
    finally { setSaving(false); }
  };

  const handleGenerateInvoice = async () => {
    if (!order) return;
    try { const res = await createShopInvoice({ shop_order_id: order.id }).unwrap(); if (res.success) navigate(`/shop-invoice/${res.data.id}`); }
    catch (e: any) { showErrorMessage(e.data?.message || "Failed to create invoice"); }
  };

  const handlePrint = () => {
    if (!order) return;
    const shopName = shop?.customerCompanyName || "Restaurant";
    const contactName = [shop?.customerFirstName, shop?.customerLastName].filter(Boolean).join(" ");
    const shopAddress = [shop?.customerStreetNumber, shop?.customerStreetName].filter(Boolean).join(" ");
    const shopCity = [shop?.customerCity, shop?.customerProvince, shop?.customerPostalCode].filter(Boolean).join(", ");
    const shopPhone = shop?.customerCompanyPhoneNumebr || shop?.customerMobileNum || "";
    const shopEmail = shop?.customerCompanyEmail || shop?.customerEmail || "";
    const invoiceDate = dayjs(order.created_at).format("MMMM D, YYYY");
    const invoiceNum = `INV-${String(order.id).padStart(5, "0")}`;
    const w = window.open("", "_blank", "width=800,height=900");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>${invoiceNum}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;color:#2d2d2d;background:#fff}.invoice{max-width:800px;margin:0 auto;padding:48px 40px}.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;padding-bottom:24px;border-bottom:3px solid #009688}.header-left h1{font-size:28px;font-weight:800;color:#009688}.header-left p{font-size:12px;color:#888;margin-top:4px}.header-right{text-align:right}.header-right .inv-num{font-size:20px;font-weight:700;color:#009688}.header-right .inv-date{font-size:13px;color:#666;margin-top:4px}.header-right .inv-status{display:inline-block;margin-top:8px;padding:4px 14px;border-radius:20px;font-size:11px;font-weight:700;text-transform:uppercase;background:#e8f5e9;color:#2e7d32}.bill-section{display:flex;justify-content:space-between;margin-bottom:32px}.bill-box{flex:1}.bill-box h3{font-size:10px;text-transform:uppercase;letter-spacing:1px;color:#999;margin-bottom:8px;font-weight:700}.bill-box p{font-size:13px;line-height:1.7;color:#444}.bill-box .name{font-size:15px;font-weight:700;color:#009688}.items-table{width:100%;border-collapse:collapse;margin-bottom:24px}.items-table thead th{text-align:left;padding:12px 8px;font-size:10px;text-transform:uppercase;letter-spacing:.8px;color:#999;font-weight:700;border-bottom:2px solid #e0e0e0}.items-table thead th.right{text-align:right}.items-table tbody td{padding:12px 8px;font-size:13px;border-bottom:1px solid #f0f0f0;color:#444}.items-table tbody td.right{text-align:right}.items-table tbody td.product{font-weight:600;color:#2d2d2d}.items-table tbody tr:last-child td{border-bottom:2px solid #e0e0e0}.totals{display:flex;justify-content:flex-end;margin-bottom:32px}.totals-box{width:260px}.totals-row{display:flex;justify-content:space-between;padding:8px 0;font-size:13px;color:#666}.totals-row.grand{border-top:2px solid #009688;padding-top:12px;margin-top:4px}.totals-row.grand span{font-size:18px;font-weight:800;color:#009688}.notes{padding:16px 20px;background:#f1f8f7;border-radius:8px;border-left:4px solid #009688;margin-bottom:32px}.notes h4{font-size:11px;text-transform:uppercase;letter-spacing:.8px;color:#009688;margin-bottom:6px;font-weight:700}.notes p{font-size:13px;color:#555;line-height:1.6}.footer{text-align:center;padding-top:24px;border-top:1px solid #e0e0e0}.footer p{font-size:11px;color:#bbb}@media print{body{padding:0}.invoice{padding:24px 20px}}</style></head><body><div class="invoice"><div class="header"><div class="header-left"><h1>INVOICE</h1><p>Order confirmation & delivery summary</p></div><div class="header-right"><div class="inv-num">${invoiceNum}</div><div class="inv-date">${invoiceDate}</div><div class="inv-status">${editStatus}</div></div></div><div class="bill-section"><div class="bill-box"><h3>Bill To</h3><p class="name">${shopName}</p>${contactName?`<p>${contactName}</p>`:""}${shopAddress?`<p>${shopAddress}</p>`:""}${shopCity?`<p>${shopCity}</p>`:""}${shopPhone?`<p>${shopPhone}</p>`:""}${shopEmail?`<p>${shopEmail}</p>`:""}</div><div class="bill-box" style="text-align:right"><h3>Order Details</h3><p>Order ID: #${order.id}</p><p>Date: ${invoiceDate}</p><p>Items: ${editItems.length}</p></div></div><table class="items-table"><thead><tr><th>#</th><th>Product</th><th class="right">Qty</th><th class="right">Unit</th><th class="right">Price</th><th class="right">Subtotal</th></tr></thead><tbody>${editItems.map((it,i)=>`<tr><td>${i+1}</td><td class="product">${it.name}</td><td class="right">${it.qty}</td><td class="right">${(it as any).unit||"-"}</td><td class="right">$${it.price.toFixed(2)}</td><td class="right">$${it.subtotal.toFixed(2)}</td></tr>`).join("")}</tbody></table><div class="totals"><div class="totals-box"><div class="totals-row"><span>Subtotal</span><span>$${editTotal.toFixed(2)}</span></div><div class="totals-row grand"><span>Total</span><span>$${editTotal.toFixed(2)}</span></div></div></div>${editNotes?`<div class="notes"><h4>Notes</h4><p>${editNotes.replace(/\n/g,"<br>")}</p></div>`:""}<div class="footer"><p>Thank you for your order</p></div></div><script>window.onload=function(){window.print()}</script></body></html>`);
    w.document.close();
  };

  const handleAssignRoute = async () => {
    if (!selectedRouteId || !selectedDeliveryDate) { showErrorMessage("Select a route and delivery date"); return; }
    setAssigningRoute(true);
    try {
      const res = await fetch(`${API_URL}/shop-orders/${orderId}/assign-route`, { method: "POST", headers, body: JSON.stringify({ route_id: selectedRouteId, delivery_date: selectedDeliveryDate }) });
      const data = await res.json();
      if (data.success) { showMessage(data.message || "Route assigned"); setRouteAssignment(data.data); setRouteDialog(false); setRouteWarning(""); }
      else showErrorMessage(data.message || "Failed");
    } catch (e: any) { showErrorMessage(e.message); }
    finally { setAssigningRoute(false); }
  };

  const isLateOrder = order ? dayjs(order.created_at).isBefore(dayjs(), "day") && order.status === "pending" : false;
  const isCancelled = order?.status === "cancelled";
  const activeStep = STATUS_FLOW.indexOf(order?.status || "pending");

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}><CircularProgress sx={{ color: "#009688" }} /></Box>;
  if (!order) return <Box sx={{ p: 4, textAlign: "center" }}><Typography color="error" fontSize={16}>Order not found</Typography><Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button></Box>;

  const sm = STATUS_MAP[order.status] || STATUS_MAP.pending;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%" }}>

      {/* ── HEADER ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#e0e0e0" } }}><ArrowBackIcon /></IconButton>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontWeight={700} fontSize={20}>Order #{order.id}</Typography>
              <Chip label={sm.label} size="small" sx={{ bgcolor: sm.bg, color: sm.color, fontWeight: 700, fontSize: 11, height: 24, borderRadius: "12px" }} />
              {isLateOrder && <Chip icon={<WarningAmberIcon sx={{ fontSize: 14 }} />} label="Overdue" size="small" sx={{ bgcolor: "#FFEBEE", color: "#C62828", fontWeight: 600, fontSize: 10, height: 22 }} />}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.25 }}>
              <Typography fontSize={12} color="text.secondary">{shop?.customerCompanyName || "Restaurant"}</Typography>
              <Typography fontSize={12} color="#ccc">|</Typography>
              <Typography fontSize={12} color="text.secondary">{dayjs(order.created_at).format("MMM D, YYYY h:mm A")}</Typography>
              {routeAssignment && (
                <>
                  <Typography fontSize={12} color="#ccc">|</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                    <LocalShippingIcon sx={{ fontSize: 12, color: "#009688" }} />
                    <Typography fontSize={12} color="#009688" fontWeight={600}>{dayjs(routeAssignment.delivery_date).format("ddd, MMM D")} &middot; {routeAssignment.route_name}</Typography>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Tooltip title="Print Order"><Button variant="outlined" size="small" startIcon={<PrintIcon sx={{ fontSize: 16 }} />} onClick={handlePrint}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 12, borderColor: "#ddd", color: "#555" }}>Print</Button></Tooltip>
          <Tooltip title="Generate Invoice"><Button variant="contained" size="small" startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />} onClick={handleGenerateInvoice}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 12, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" } }}>Invoice</Button></Tooltip>
          {!isCancelled && (
            <Tooltip title="Cancel Order"><Button variant="outlined" size="small" startIcon={<CancelIcon sx={{ fontSize: 16 }} />} onClick={() => setCancelDialog(true)}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 12, borderColor: "#ef5350", color: "#d32f2f", "&:hover": { bgcolor: "#ffebee" } }}>Cancel</Button></Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Status Flow ── */}
      {!isCancelled && (
        <Paper elevation={0} sx={{ mb: 2.5, p: 1.5, borderRadius: 2.5, border: "1px solid #e8e8e8" }}>
          <Stepper activeStep={activeStep >= 0 ? activeStep : 0} alternativeLabel
            sx={{ "& .MuiStepLabel-label": { fontSize: 11, fontWeight: 600 }, "& .MuiStepIcon-root.Mui-active": { color: "#009688" }, "& .MuiStepIcon-root.Mui-completed": { color: "#009688" } }}>
            {STATUS_FLOW_LABELS.map(label => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
          </Stepper>
        </Paper>
      )}

      {/* ── Warnings ── */}
      {itemsWithWarning.length > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}>
          <strong>{itemsWithWarning.length} item{itemsWithWarning.length > 1 ? "s" : ""} with $0.00 price:</strong> {itemsWithWarning.map(it => it.name).join(", ")}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", lg: "row" } }}>

        {/* ══ LEFT: Order Items (70%) ══ */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ borderRadius: 3, border: "1px solid #e8e8e8", overflow: "hidden" }}>
            <Box sx={{ bgcolor: "#009688", px: 2.5, py: 1.25, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography fontWeight={700} fontSize={14} color="#fff">Order Items</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {!isCancelled && (
                  <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setAddProductDialog(true)}
                    sx={{ color: "#fff", fontSize: 11, textTransform: "none", fontWeight: 600, bgcolor: "rgba(255,255,255,0.15)", borderRadius: 1.5, "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
                    Add Product
                  </Button>
                )}
                <Typography fontSize={12} color="rgba(255,255,255,0.7)" sx={{ display: "flex", alignItems: "center" }}>{editItems.length} items</Typography>
              </Box>
            </Box>

            {order.portal_note && (
              <Alert severity="info" icon={<NoteAddIcon />} sx={{ mx: 2, mt: 2, borderRadius: 2, fontSize: 12 }}>
                <strong>Restaurant Note:</strong> {order.portal_note}
              </Alert>
            )}

            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", width: 36, textTransform: "uppercase" }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", textTransform: "uppercase" }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", width: 85, textTransform: "uppercase" }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", width: 55, textTransform: "uppercase" }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", width: 100, textTransform: "uppercase" }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 10, color: "#aaa", width: 90, textTransform: "uppercase", textAlign: "right" }}>Subtotal</TableCell>
                    <TableCell sx={{ width: 64 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {editItems.map((item, i) => (
                    <TableRow key={i} sx={{ "&:hover": { bgcolor: "#fafafa" }, bgcolor: item.price === 0 ? "#FFFDE7" : undefined }}>
                      <TableCell sx={{ fontSize: 12, color: "#bbb" }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={500}>{item.name}</Typography>
                        {item.price === 0 && <Typography fontSize={10} color="#E65100" fontWeight={600}>Missing price</Typography>}
                      </TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={item.qty} onChange={(e) => handleQtyChange(i, e.target.value)}
                          inputProps={{ min: 0, step: "0.1" }} disabled={isCancelled}
                          sx={{ width: 70, "& .MuiOutlinedInput-root": { borderRadius: 1.5 }, "& input": { fontSize: 13, py: 0.5 } }} />
                      </TableCell>
                      <TableCell><Typography fontSize={12} color="text.secondary">{(item as any).unit || "-"}</Typography></TableCell>
                      <TableCell>
                        <TextField size="small" type="number" value={item.price} onChange={(e) => handlePriceChange(i, e.target.value)}
                          inputProps={{ min: 0, step: "0.01" }} disabled={isCancelled}
                          InputProps={{ startAdornment: <Typography sx={{ fontSize: 12, color: "#999", mr: 0.25 }}>$</Typography> }}
                          sx={{ width: 85, "& .MuiOutlinedInput-root": { borderRadius: 1.5 }, "& input": { fontSize: 13, py: 0.5 } }} />
                      </TableCell>
                      <TableCell sx={{ textAlign: "right" }}>
                        <Typography fontSize={13} fontWeight={600} color={item.subtotal > 0 ? "#2e7d32" : "#999"}>${item.subtotal.toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell>
                        {!isCancelled && (
                          <Box sx={{ display: "flex", gap: 0.25 }}>
                            <Tooltip title="Duplicate"><IconButton size="small" onClick={() => handleDuplicateItem(i)} sx={{ color: "#ccc", "&:hover": { color: "#1565C0" } }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                            <Tooltip title="Remove"><IconButton size="small" onClick={() => handleRemoveItem(i)} sx={{ color: "#ccc", "&:hover": { color: "#d32f2f" } }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* ── Sticky Totals ── */}
            <Box sx={{ p: 2, borderTop: "2px solid #e8e8e8", bgcolor: "#FAFAFA", position: "sticky", bottom: 0 }}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Box sx={{ width: 220 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.5 }}>
                    <Typography fontSize={12} color="text.secondary">Subtotal ({editItems.length} items)</Typography>
                    <Typography fontSize={12}>${editTotal.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 0.75 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography fontWeight={700} fontSize={15}>Total</Typography>
                    <Typography fontWeight={800} fontSize={17} color="#009688">${editTotal.toFixed(2)}</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Card>
        </Box>

        {/* ══ RIGHT: Sidebar (30%, sticky) ══ */}
        <Box sx={{ width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
          <Box sx={{ position: { lg: "sticky" }, top: { lg: 16 } }}>

            {/* Restaurant */}
            <Card sx={{ borderRadius: 2.5, border: "1px solid #e8e8e8", mb: 2, overflow: "hidden" }}>
              <Box sx={{ bgcolor: "#009688", px: 2, py: 1 }}><Typography fontWeight={700} fontSize={12} color="#fff">Restaurant</Typography></Box>
              <Box sx={{ p: 2 }}>
                <Typography fontWeight={700} fontSize={14}>{shop?.customerCompanyName || "—"}</Typography>
                <Typography fontSize={11} color="text.secondary">{[shop?.customerFirstName, shop?.customerLastName].filter(Boolean).join(" ")}</Typography>
                {(shop?.customerCompanyPhoneNumebr || shop?.customerMobileNum) && <Typography fontSize={11} color="text.secondary" mt={0.25}>{shop?.customerCompanyPhoneNumebr || shop?.customerMobileNum}</Typography>}
                {(shop?.customerCompanyEmail || shop?.customerEmail) && <Typography fontSize={11} color="text.secondary" mt={0.25}>{shop?.customerCompanyEmail || shop?.customerEmail}</Typography>}
              </Box>
            </Card>

            {/* Status */}
            <Card sx={{ borderRadius: 2.5, border: "1px solid #e8e8e8", mb: 2, p: 2 }}>
              <Typography fontWeight={600} fontSize={12} mb={1}>Status</Typography>
              <Select fullWidth size="small" value={editStatus} onChange={(e) => setEditStatus(e.target.value)} disabled={isCancelled}
                sx={{ borderRadius: 2, fontSize: 12 }}>
                {STATUSES.map(s => <MenuItem key={s} value={s} sx={{ fontSize: 12 }}><Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_MAP[s]?.color || "#999" }} />{STATUS_MAP[s]?.label || s}</Box></MenuItem>)}
              </Select>
            </Card>

            {/* Delivery */}
            <Card sx={{ borderRadius: 2.5, border: "1px solid #e8e8e8", mb: 2, overflow: "hidden" }}>
              <Box sx={{ bgcolor: "#009688", px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography fontWeight={700} fontSize={12} color="#fff"><LocalShippingIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: "middle" }} />Delivery</Typography>
                <Button size="small" onClick={() => { setRouteDialog(true); setSelectedRouteId(routeAssignment?.route_id || ""); setSelectedDeliveryDate(routeAssignment?.delivery_date || dayjs().format("YYYY-MM-DD")); }}
                  sx={{ color: "#fff", fontSize: 10, textTransform: "none", fontWeight: 600, minWidth: 0, py: 0 }}>{routeAssignment ? "Change" : "Assign"}</Button>
              </Box>
              <Box sx={{ p: 2 }}>
                {routeWarning && <Alert severity="warning" sx={{ mb: 1, borderRadius: 1.5, fontSize: 11, py: 0 }}>{routeWarning}</Alert>}
                {routeAssignment ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                      <AltRouteIcon sx={{ fontSize: 14, color: "#009688" }} />
                      <Typography fontSize={13} fontWeight={600}>{routeAssignment.route_name}</Typography>
                    </Box>
                    <Typography fontSize={12} color="text.secondary">{dayjs(routeAssignment.delivery_date).format("dddd, MMM D, YYYY")}</Typography>
                    <Chip label={routeAssignment.status} size="small" sx={{ mt: 0.75, fontSize: 10, fontWeight: 600, bgcolor: "#E0F2F1", color: "#009688", height: 20 }} />
                  </>
                ) : (
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <Typography fontSize={12} color="#bbb">No route assigned</Typography>
                  </Box>
                )}
              </Box>
            </Card>

            {/* Payment Summary */}
            <Card sx={{ borderRadius: 2.5, border: "1px solid #e8e8e8", mb: 2, p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                <PaymentIcon sx={{ fontSize: 14, color: "#999" }} />
                <Typography fontWeight={600} fontSize={12}>Payment</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography fontSize={12} color="text.secondary">Order Total</Typography>
                <Typography fontSize={12} fontWeight={700}>${editTotal.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography fontSize={12} color="text.secondary">Status</Typography>
                <Chip label="Pending Invoice" size="small" sx={{ fontSize: 10, fontWeight: 600, bgcolor: "#FFF8E1", color: "#F57F17", height: 20 }} />
              </Box>
            </Card>

            {/* Credit Status */}
            {creditStatus && creditStatus.credit_limit > 0 && (
              <Card sx={{ borderRadius: 2.5, border: `1px solid ${creditStatus.color === "red" ? "#FFCDD2" : creditStatus.color === "orange" ? "#FFE0B2" : "#C8E6C9"}`, mb: 2, p: 2, bgcolor: creditStatus.color === "red" ? "#FFF8F6" : creditStatus.color === "orange" ? "#FFFDF5" : undefined }}>
                <Typography fontWeight={600} fontSize={12} mb={1}>Credit Status</Typography>
                {[
                  { label: "Credit Limit", value: `$${creditStatus.credit_limit.toFixed(2)}` },
                  { label: "Outstanding", value: `$${creditStatus.outstanding.toFixed(2)}` },
                  { label: "After This Order", value: `$${(creditStatus.outstanding + editTotal).toFixed(2)}`, color: (creditStatus.outstanding + editTotal) > creditStatus.credit_limit ? "#C62828" : undefined },
                  { label: "Available", value: creditStatus.available !== null ? `$${creditStatus.available.toFixed(2)}` : "Unlimited" },
                ].map(r => (
                  <Box key={r.label} sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}>
                    <Typography fontSize={11} color="text.secondary">{r.label}</Typography>
                    <Typography fontSize={11} fontWeight={600} color={r.color || "#333"}>{r.value}</Typography>
                  </Box>
                ))}
                {/* Usage bar */}
                <Box sx={{ mt: 1, height: 6, bgcolor: "#f0f0f0", borderRadius: 3, overflow: "hidden" }}>
                  <Box sx={{ height: "100%", width: `${Math.min(100, creditStatus.usage_percent)}%`, bgcolor: creditStatus.color === "red" ? "#C62828" : creditStatus.color === "orange" ? "#E65100" : "#2E7D32", borderRadius: 3, transition: "width 0.3s" }} />
                </Box>
                <Typography fontSize={10} color={creditStatus.color === "red" ? "#C62828" : "#999"} mt={0.5} textAlign="right">{creditStatus.usage_percent}% used</Typography>
                {creditStatus.has_overdue && (
                  <Alert severity="error" sx={{ mt: 1, borderRadius: 1.5, fontSize: 11, py: 0 }}>Overdue invoices — ordering blocked</Alert>
                )}
                {creditStatus.is_blocked && !creditStatus.has_overdue && (
                  <Alert severity="warning" sx={{ mt: 1, borderRadius: 1.5, fontSize: 11, py: 0 }}>Credit limit exceeded</Alert>
                )}
              </Card>
            )}

            {/* Notes */}
            <Card sx={{ borderRadius: 2.5, border: "1px solid #e8e8e8", mb: 2, p: 2 }}>
              <Typography fontWeight={600} fontSize={12} mb={1}>Notes</Typography>
              {noteHistory.length > 0 && (
                <Box sx={{ mb: 1.5, maxHeight: 120, overflow: "auto" }}>
                  {noteHistory.map((n, i) => (
                    <Box key={i} sx={{ py: 0.5, borderBottom: "1px solid #f5f5f5" }}>
                      <Typography fontSize={11} color="#555">{n}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <TextField size="small" fullWidth placeholder="Add a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddNote(); } }}
                  disabled={isCancelled} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 12 } }} />
                <IconButton size="small" onClick={handleAddNote} disabled={!newNote.trim() || isCancelled}
                  sx={{ bgcolor: "#009688", color: "#fff", borderRadius: 1.5, width: 32, height: 32, "&:hover": { bgcolor: "#00796B" }, "&.Mui-disabled": { bgcolor: "#e0e0e0" } }}>
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Card>

            {/* Timeline */}
            <Card sx={{ borderRadius: 2.5, border: "1px solid #e8e8e8", mb: 2, p: 2 }}>
              <Typography fontWeight={600} fontSize={12} mb={1}>Timeline</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#009688" }} />
                <Typography fontSize={11}>Created {dayjs(order.created_at).format("MMM D, h:mm A")}</Typography>
              </Box>
              {order.updated_at !== order.created_at && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#FF9800" }} />
                  <Typography fontSize={11}>Updated {dayjs(order.updated_at).format("MMM D, h:mm A")}</Typography>
                </Box>
              )}
            </Card>

            {/* Actions */}
            {!isCancelled && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Button variant="contained" fullWidth startIcon={<SaveIcon />} disabled={saving} onClick={() => handleSave()}
                  sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, py: 1, fontSize: 13, bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" } }}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
                {order.status === "pending" && (
                  <Button variant="contained" fullWidth startIcon={<CheckCircleIcon />} disabled={saving} onClick={() => handleSave("confirmed")}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, py: 1, fontSize: 13, bgcolor: "#2E7D32", "&:hover": { bgcolor: "#1B5E20" } }}>
                    Confirm & Finalize
                  </Button>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── DIALOGS ── */}
      <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Cancel Order #{order.id}</DialogTitle>
        <DialogContent>
          <Typography fontSize={13} color="text.secondary" mb={2}>This action cannot be undone.</Typography>
          <TextField fullWidth multiline rows={3} size="small" label="Reason (optional)" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCancelDialog(false)} sx={{ borderRadius: 2, textTransform: "none" }}>Keep Order</Button>
          <Button variant="contained" onClick={handleCancel} disabled={saving} sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#d32f2f", "&:hover": { bgcolor: "#b71c1c" } }}>{saving ? "Cancelling..." : "Cancel Order"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={routeDialog} onClose={() => setRouteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>{routeAssignment ? "Change Route" : "Assign Route"}</DialogTitle>
        <DialogContent>
          {availableRoutes.length === 0 ? (
            <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 13 }}>No routes assigned to this restaurant.</Alert>
          ) : (
            <>
              <Select fullWidth size="small" value={selectedRouteId} onChange={(e) => setSelectedRouteId(e.target.value as number)} displayEmpty sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>
                <MenuItem value="" disabled sx={{ fontSize: 13 }}>Select Route</MenuItem>
                {availableRoutes.map((r: any) => <MenuItem key={r.id} value={r.id} sx={{ fontSize: 13 }}>{r.name}</MenuItem>)}
              </Select>
              <TextField fullWidth size="small" type="date" label="Delivery Date" value={selectedDeliveryDate} onChange={(e) => setSelectedDeliveryDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13 } }} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRouteDialog(false)} sx={{ borderRadius: 2, textTransform: "none" }}>Cancel</Button>
          {availableRoutes.length > 0 && <Button variant="contained" onClick={handleAssignRoute} disabled={assigningRoute || !selectedRouteId || !selectedDeliveryDate} sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" } }}>{assigningRoute ? "Assigning..." : "Assign"}</Button>}
        </DialogActions>
      </Dialog>

      <Dialog open={addProductDialog} onClose={() => setAddProductDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>Add Product</DialogTitle>
        <DialogContent>
          <TextField fullWidth size="small" label="Product Name" value={newProductName} onChange={(e) => setNewProductName(e.target.value)} sx={{ mb: 2, mt: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField size="small" label="Qty" type="number" value={newProductQty} onChange={(e) => setNewProductQty(e.target.value)} sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <TextField size="small" label="Price" type="number" value={newProductPrice} onChange={(e) => setNewProductPrice(e.target.value)} InputProps={{ startAdornment: <Typography sx={{ fontSize: 12, color: "#999", mr: 0.25 }}>$</Typography> }} sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
            <Select size="small" value={newProductUnit} onChange={(e) => setNewProductUnit(e.target.value)} sx={{ minWidth: 70, borderRadius: 2, fontSize: 13 }}>
              {["lb", "kg", "unit", "box", "bag", "bunch", "dozen"].map(u => <MenuItem key={u} value={u} sx={{ fontSize: 12 }}>{u}</MenuItem>)}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddProductDialog(false)} sx={{ borderRadius: 2, textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddProduct} disabled={!newProductName.trim()} sx={{ borderRadius: 2, textTransform: "none", bgcolor: "#009688", "&:hover": { bgcolor: "#00796B" } }}>Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
